import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, errorMessage, post } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Notice, TextAreaField } from '../../components/ui.js';
import { LISTS, marketLabel, optionLabel } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import type { LocalRecord } from '../../offline/store.js';
import { fullName } from '../people/data.js';
import { createSpeech, type Speech } from './speech.js';

interface Filled {
  interest: string | null;
  role: string | null;
  pain: string | null;
  applications: string[];
  country: string | null;
  summary: string | null;
  project: boolean;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const MAX_SECONDS = 60;

/**
 * Qualify by voice (brief section 8; prototype: openVoice): the seller says
 * what they learned; Claude fills the contact's answers (interest, role,
 * pain, operating markets, country); the seller checks, then saves them,
 * or creates a lead: Start the lead opens with them (its five answers stay
 * required). Mounted at /people/contacts/:id/voice.
 */
export function QualifyByVoice({ speech: given }: { speech?: Speech }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { store, save } = useOffline();
  const speech = useMemo(() => given ?? createSpeech(), [given]);
  const [contact, setContact] = useState<LocalRecord | null>(null);
  const [state, setState] = useState<'idle' | 'rec' | 'reading' | 'done'>('idle');
  const [seconds, setSeconds] = useState(0);
  const [words, setWords] = useState('');
  const [filled, setFilled] = useState<Filled | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const back = () => navigate(`/people/contacts/${id}`, { replace: true });

  useEffect(() => {
    let current = true;
    void store?.getRecord('contact', id).then((c) => current && setContact(c ?? null));
    return () => {
      current = false;
      if (timer.current) clearInterval(timer.current);
      speech.stop();
    };
  }, [store, id, speech]);

  const name = contact ? fullName(contact) || str(contact.company) : '';
  const first = str(contact?.first_name) || str(contact?.company) || 'them';

  async function read(transcript: string) {
    if (transcript.trim().length < 3) {
      setState('idle');
      setError('Nothing was heard. Try again, or type it.');
      return;
    }
    setState('reading');
    setError(null);
    try {
      setFilled(await post<Filled>('/voice/qualify', { transcript, name }));
      setState('done');
    } catch (err) {
      setState('idle');
      setError(qualifyError(err));
    }
  }

  async function record() {
    if (state === 'rec') {
      speech.stop();
      return;
    }
    setError(null);
    setFilled(null);
    setWords('');
    setSeconds(0);
    setState('rec');
    timer.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) speech.stop();
        return s + 1;
      });
    }, 1000);
    try {
      const said = await speech.listen({ continuous: true, onWords: setWords });
      setWords(said);
      await read(said);
    } catch {
      setState('idle');
      setError('The microphone is not available. Type what you learned instead.');
    } finally {
      if (timer.current) clearInterval(timer.current);
    }
  }

  async function apply(createLead: boolean) {
    if (!filled || saving || !store) return;
    setSaving(true);
    // The contact as the device keeps it now (it may have arrived meanwhile).
    const contact = (await store.getRecord('contact', id)) ?? null;
    if (!contact) {
      setError('This contact is not on this device yet. Try again in a moment.');
      setSaving(false);
      return;
    }
    const fields: Record<string, unknown> = {};
    if (filled.interest) fields.interest = filled.interest;
    if (filled.role) fields.role = filled.role;
    if (filled.pain) fields.pain = filled.pain;
    if (filled.applications.length) fields.applications = filled.applications;
    if (filled.country && !str(contact.country)) fields.country = filled.country;
    fields.note = [str(contact.note), `Voice note: ${words.trim()}`]
      .filter(Boolean)
      .join('\n')
      .slice(0, 4000);
    try {
      await save({
        opId: newId(),
        entity: 'contact',
        id: contact.id,
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields,
      });
      if (createLead) navigate(`/people/contacts/${id}/lead`, { replace: true });
      else {
        toast('Qualification saved');
        back();
      }
    } catch {
      setError('This device could not save. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  const time =
    state === 'rec'
      ? `0:${String(seconds).padStart(2, '0')}, tap to stop`
      : state === 'done'
        ? 'Done'
        : 'Tap to start';

  return (
    <Dialog
      title="Qualify by voice"
      onClose={back}
      footer={
        state === 'done' && filled ? (
          <>
            <button type="button" className="btn" onClick={() => void record()}>
              Record again
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={saving || !store}
              onClick={() => void apply(filled.project)}
            >
              {filled.project ? 'Create a lead' : 'Save the answers'}
            </button>
          </>
        ) : (
          <button type="button" className="btn" onClick={back}>
            Cancel
          </button>
        )
      }
    >
      <p className="voice-intro">
        Say what you learned about {first}: active project or just browsing, their role, what they
        sort and what frustrates them, and the next step. 20 seconds is enough.
      </p>
      <div className={`voice${state === 'rec' ? ' rec' : ''}${state === 'reading' ? ' busy' : ''}`}>
        <button
          type="button"
          className="mic"
          aria-label={state === 'rec' ? 'Stop recording' : 'Start recording'}
          disabled={!speech.canListen || state === 'reading'}
          onClick={() => void record()}
        >
          <Icon name="mic" />
        </button>
        <div className="wave" aria-hidden="true">
          {Array.from({ length: 18 }, (_, i) => (
            <i key={i} />
          ))}
        </div>
        <span className="vtime">{speech.canListen ? time : 'This browser cannot listen'}</span>
      </div>
      {!speech.canListen && state !== 'done' && (
        <TypedNote
          onRead={(text) => {
            setWords(text);
            void read(text);
          }}
        />
      )}
      {state === 'reading' && (
        <div className="sstep run">
          <span className="si" />
          <span>Filling the qualification</span>
        </div>
      )}
      {state === 'done' && filled && (
        <>
          <div className="brief qv-quote">&ldquo;{words.trim()}&rdquo;</div>
          <div className="formsec">
            <h3>What Claude filled in</h3>
            <dl className="kv">
              <dt>Interest</dt>
              <dd>{optionLabel(LISTS.interestOptions, filled.interest) || 'Not said'}</dd>
              <dt>Role</dt>
              <dd>{optionLabel(LISTS.roleOptions, filled.role) || 'Not said'}</dd>
              <dt>Pain</dt>
              <dd>{filled.pain || 'Not said'}</dd>
              <dt>Operating market(s)</dt>
              <dd>{filled.applications.map(marketLabel).join(', ') || 'Not said'}</dd>
              {filled.country && (
                <>
                  <dt>Country</dt>
                  <dd>{filled.country}</dd>
                </>
              )}
              <dt>Outcome</dt>
              <dd>
                <b>{filled.project ? 'A project: create a lead' : 'No project for now'}</b>
              </dd>
            </dl>
          </div>
          <p className="note voice-intro">You check everything before saving.</p>
        </>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      {error && state === 'idle' && words.trim() && (
        <div className="brief qv-quote">&ldquo;{words.trim()}&rdquo;</div>
      )}
    </Dialog>
  );
}

/**
 * What went wrong, said usefully (round 2: never "Something went wrong").
 * What was said stays on screen, so nothing is lost: Record again, or type.
 */
export function qualifyError(err: unknown): string {
  const after = 'What you said is kept below: record again, or type the answers on the contact.';
  if (!(err instanceof ApiError)) return `This device could not send it. ${after}`;
  if (err.code === 'offline') return `No network: Claude cannot fill it now. ${after}`;
  if (err.status === 503) return `Claude is not available right now. ${after}`;
  if (err.status === 400) return `Claude could not read this dictation. ${after}`;
  if (err.status === 502 || err.status === 504) {
    return `Claude took too long to answer. Try again in a moment. ${after}`;
  }
  if (err.status === 401 || err.status === 403 || err.code === 'csrf') return errorMessage(err);
  return `The portal could not fill it (error ${err.status}). ${after}`;
}

/** Where the browser cannot listen: the same, typed. */
function TypedNote({ onRead }: { onRead(text: string): void }) {
  const [text, setText] = useState('');
  return (
    <>
      <TextAreaField
        label="What you learned"
        value={text}
        rows={4}
        onChange={(e) => setText(e.target.value)}
      />
      <button
        type="button"
        className="btn primary voice-start"
        disabled={text.trim().length < 3}
        onClick={() => onRead(text)}
      >
        Fill the qualification
      </button>
    </>
  );
}
